import { useEffect, useState } from "react";
import { firebaseIdle } from "@/lib/firebase/app";
import { getDb, loadFirestoreModule } from "@/lib/firebase/db";

/**
 * Klantwaardering + reviews.
 *
 * Twee sporen, zoals grote webshops (Coolblue/bol) het doen:
 *  1. Een reviewplatform (Kiyoh) is de bron van waarheid zodra 'm gekoppeld is. Vul KIYOH in met
 *     de gegevens uit je Kiyoh-dashboard → de reviewpagina toont dan het echte cijfer/widget en
 *     "Schrijf een review" gaat naar de Kiyoh-uitnodigingspagina.
 *  2. Zolang Kiyoh nog niet gekoppeld is, werkt er een eigen (in-house) reviewformulier: inzendingen
 *     gaan via de submitReview-Cloud Function naar Firestore en verschijnen na goedkeuring op de pagina.
 */

/**
 * USP-tekst in de balk zolang er nog géén Kiyoh-cijfer is.
 *
 * Bewust géén cijfer of "uitstekend beoordeeld": dat zou een waardering claimen die we niet kunnen
 * onderbouwen, en het spreekt de sterren ernaast tegen. Wat hier staat is wél waar en meteen
 * controleerbaar — het reviewen loopt sinds 09-08-2026 via Kiyoh.
 *
 * Zodra Kiyoh een cijfer heeft, vervangen UspRow en UspTicker deze tekst automatisch door
 * "Beoordeeld met een 9,4", live uit [[src/lib/kiyoh-stats.ts]] — geen constante die iemand met de
 * hand moet bijwerken.
 */
export const reviewUspText = "Onafhankelijke reviews via Kiyoh";

/**
 * Kiyoh-gegevens staan in packages/core (reviews-platform.ts) omdat de Cloud Functions ze óók
 * nodig hebben voor het automatische reviewverzoek per mail. Hier alleen doorgeven, zodat alle
 * bestaande imports uit "@/lib/reviews" blijven werken.
 */
export { KIYOH, kiyohCanWrite, kiyohHasWidget, kiyohWidgetSrc } from "@glastotaal/core";

export type Review = {
  id: string;
  name: string;
  rating: number;
  title?: string;
  message: string;
  /** Artikel waar de beoordeling over gaat; leeg = algemene winkelbeoordeling. */
  articleSlug?: string;
  createdAt?: number; // ms sinds epoch
  /** Kwam via Kiyoh binnen (onafhankelijk geverifieerd) i.p.v. via ons eigen formulier. */
  viaKiyoh?: boolean;
};

export type ReviewInput = {
  name: string;
  email: string;
  rating: number;
  title?: string;
  message: string;
  orderNumber?: string;
  /** Artikel-slug bij een productbeoordeling (vanaf de productpagina). */
  articleSlug?: string;
};

/**
 * Goedgekeurde reviews uit Firestore (publiek leesbaar). Lege lijst bij fouten/nog geen reviews.
 * `articleSlug` gezet → alleen de beoordelingen van dát product (voor de productpagina en de
 * sterren in Google); weggelaten → alle reviews, zoals de reviewspagina ze toont.
 */
async function readApprovedReviews(max = 30, articleSlug?: string): Promise<Review[]> {
  try {
    const [{ collection, getDocs, limit, orderBy, query, where }, db] = await Promise.all([loadFirestoreModule(), getDb()]);
    const filters = [
      where("approved", "==", true),
      ...(articleSlug ? [where("articleSlug", "==", articleSlug)] : []),
    ];
    const q = query(collection(db, "reviews"), ...filters, orderBy("createdAt", "desc"), limit(max));
    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const r = d.data() as Record<string, unknown>;
      const ts = r.createdAt as { toMillis?: () => number } | undefined;
      return {
        id: d.id,
        name: String(r.name ?? ""),
        rating: Number(r.rating ?? 0),
        title: r.title ? String(r.title) : undefined,
        message: String(r.message ?? ""),
        articleSlug: r.articleSlug ? String(r.articleSlug) : undefined,
        createdAt: ts?.toMillis ? ts.toMillis() : undefined,
      };
    });
  } catch {
    return [];
  }
}

/**
 * De productbeoordelingen die via Kiyoh binnenkwamen, uit `productReviews/{slug}`.
 *
 * Geschreven door de nachtelijke `syncKiyohProductReviews`-functie; publiek leesbaar, want dit is
 * precies wat op de artikelpagina hoort te staan. Kiyoh's cijfers staan op 10 — hier gaan ze door
 * de deler naar onze 5-puntsschaal, zodat ze naast een eigen review in dezelfde sterren passen.
 *
 * Lege lijst bij een fout of als het artikel nog geen beoordelingen heeft: geen reviewblok is
 * beter dan een blok dat een storing als "0 sterren" toont.
 */
async function readKiyohProductReviews(articleSlug: string): Promise<Review[]> {
  try {
    const [{ doc, getDoc }, db] = await Promise.all([loadFirestoreModule(), getDb()]);
    const snap = await getDoc(doc(db, "productReviews", articleSlug));
    if (!snap.exists()) return [];
    const data = snap.data() as { reviews?: unknown[] };
    return (Array.isArray(data.reviews) ? data.reviews : []).map((raw, i) => {
      const r = raw as Record<string, unknown>;
      return {
        id: String(r.id ?? `kiyoh-${i}`),
        name: String(r.author ?? "Klant"),
        rating: Number(r.rating ?? 0) / 2,
        title: r.title ? String(r.title) : undefined,
        message: String(r.text ?? ""),
        articleSlug,
        createdAt: typeof r.date === "number" ? r.date : undefined,
        viaKiyoh: true,
      };
    });
  } catch {
    return [];
  }
}

/** Hook: goedgekeurde reviews + gemiddeld cijfer (op 5). */
export function useReviews(): { reviews: Review[]; loading: boolean; average: number | null } {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    void firebaseIdle().then(() => readApprovedReviews()).then((r) => {
      if (!alive) return;
      setReviews(r);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);
  const average = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : null;
  return { reviews, loading, average };
}

/**
 * Hook: de goedgekeurde beoordelingen van ÉÉN artikel + gemiddelde en aantal.
 *
 * Voedt twee dingen tegelijk, en dat moet samen: de zichtbare reviewsectie op de productpagina
 * én de `aggregateRating` in de Product-structuurdata. Google eist dat een rating die je in de
 * structuurdata claimt ook echt op de pagina staat — vandaar één bron voor allebei.
 */
export function useArticleReviews(articleSlug: string | undefined): {
  reviews: Review[];
  loading: boolean;
  average: number | null;
  count: number;
} {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!articleSlug) { setReviews([]); setLoading(false); return; }
    let alive = true;
    setLoading(true);
    // Twee bronnen naast elkaar: beoordelingen via Kiyoh (na een bestelling, onafhankelijk
    // gepubliceerd) én inzendingen via ons eigen formulier op de productpagina. Nieuwste eerst,
    // ongeacht waar ze vandaan komen.
    void firebaseIdle()
      .then(() => Promise.all([readApprovedReviews(30, articleSlug), readKiyohProductReviews(articleSlug)]))
      .then(([eigen, kiyoh]) => {
        if (!alive) return;
        setReviews([...kiyoh, ...eigen].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0)));
        setLoading(false);
      });
    return () => { alive = false; };
  }, [articleSlug]);
  const count = reviews.length;
  const average = count ? reviews.reduce((s, r) => s + r.rating, 0) / count : null;
  return { reviews, loading, average, count };
}
